import * as THREE from 'three';
import type { RawDataObject } from '@visualizer/types';

/**
 * Thicken a surface-like geometry by adding volume and side faces.
 * - Keeps original front faces
 * - Adds reversed back faces
 * - Detects boundary edges and stitches sides
 *
 * @param {THREE.BufferGeometry} baseGeom - The (surface) geometry to thicken
 * @param {number} thickness - World-space thickness
 * @returns {THREE.BufferGeometry} new geometry with volume
 */
export function thickenGeometry(baseGeom: THREE.BufferGeometry, thickness = 0.1): THREE.BufferGeometry {
    // Work on a clone so we don't mutate the original
    const geom = baseGeom.clone();

    // Ensure indexed; this logic relies on an index buffer
    if (!geom.getIndex()) {
        geom.setIndex([...Array(geom.attributes.position.count).keys()]);
    }

    // Ensure normals exist for vertex offset direction
    if (!geom.attributes.normal) {
        geom.computeVertexNormals();
    }

    const pos = geom.attributes.position;
    const nor = geom.attributes.normal;
    const uvAttr = geom.attributes.uv || null;
    const index = geom.index!.array;
    const vertCount = pos.count;

    // Build boundary edge set: edges that appear only once
    // We treat edges as (min,max) undirected keys
    const edgeUse = new Map(); // key -> {i0,i1,count}
    const addEdge = (a: number, b: number) => {
        const i0 = Math.min(a, b);
        const i1 = Math.max(a, b);
        const key = `${i0}_${i1}`;
        const entry = edgeUse.get(key) || { i0, i1, count: 0 };
        entry.count++;
        edgeUse.set(key, entry);
    };

    // Iterate triangles
    for (let i = 0; i < index.length; i += 3) {
        const a = index[i + 0];
        const b = index[i + 1];
        const c = index[i + 2];
        addEdge(a, b);
        addEdge(b, c);
        addEdge(c, a);
    }

    // Positions (front first, then back)
    const thick = thickness * 0.5;
    const outPositions = new Float32Array(vertCount * 2 * 3);
    const outUVs = uvAttr ? new Float32Array(vertCount * 2 * 2) : null;

    for (let i = 0; i < vertCount; i++) {
        const vx = pos.getX(i),
            vy = pos.getY(i),
            vz = pos.getZ(i);
        const nx = nor.getX(i),
            ny = nor.getY(i),
            nz = nor.getZ(i);

        // Front (offset +n * half thickness)
        outPositions[i * 3 + 0] = vx + nx * thick;
        outPositions[i * 3 + 1] = vy + ny * thick;
        outPositions[i * 3 + 2] = vz + nz * thick;

        // Back (offset -n * half thickness)
        const j = i + vertCount;
        outPositions[j * 3 + 0] = vx;
        outPositions[j * 3 + 1] = vy;
        outPositions[j * 3 + 2] = vz;

        if (outUVs) {
            outUVs[i * 2 + 0] = uvAttr.getX(i);
            outUVs[i * 2 + 1] = uvAttr.getY(i);
            outUVs[j * 2 + 0] = uvAttr.getX(i);
            outUVs[j * 2 + 1] = uvAttr.getY(i);
        }
    }

    // Indices:
    //   1) Front: same winding as original (faces outwards)
    //   2) Back: reversed winding (also faces outwards)
    //   3) Sides: stitch boundary edges into quads (two tris)
    const outIndices = [];

    // 1) Front
    for (let i = 0; i < index.length; i += 3) {
        outIndices.push(index[i + 0], index[i + 1], index[i + 2]);
    }

    // 2) Back (reverse order)
    for (let i = 0; i < index.length; i += 3) {
        const a = index[i + 0] + vertCount;
        const b = index[i + 1] + vertCount;
        const c = index[i + 2] + vertCount;
        // reverse winding
        outIndices.push(a, c, b);
    }

    // 3) Sides (boundary edges only)
    // For each boundary edge (i0, i1), create:
    //   tri A: i0F, i1F, i1B
    //   tri B: i0F, i1B, i0B
    // This winding should face outward for typical meshes.
    edgeUse.forEach(({ i0, i1, count }) => {
        if (count === 1) {
            const i0F = i0;
            const i1F = i1;
            const i0B = i0 + vertCount;
            const i1B = i1 + vertCount;

            outIndices.push(i0F, i1F, i1B);
            outIndices.push(i0F, i1B, i0B);
        }
    });

    // Assemble final geometry
    const thickGeom = new THREE.BufferGeometry();
    thickGeom.setAttribute('position', new THREE.BufferAttribute(outPositions, 3));
    if (outUVs) thickGeom.setAttribute('uv', new THREE.BufferAttribute(outUVs, 2));
    thickGeom.setIndex(outIndices);
    // thickGeom.computeVertexNormals();

    return thickGeom;
}

export function getOnlyGeometryFromRawData({ points, triangles }: RawDataObject): THREE.BufferGeometry {
    const pointCount = points.length;

    let indices: number[] = [];
    const vertices = new Float32Array(pointCount * 3);
    points.forEach((p, idx) => {
        vertices.set([p.x, p.y, p.z], idx * 3);
    });
    triangles.forEach((tri, _) => {
        indices.push(tri.A);
        indices.push(tri.C);
        indices.push(tri.B);
    });

    const geometry = new THREE.BufferGeometry();
    geometry.setIndex(indices);
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));

    return geometry;
}
